import { useRef } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import { deleteCampaignMediaByUrl, uploadCampaignMedia } from 'api/media';
import * as Form from 'components/form';
import { Upload, UploadRef } from 'components/upload';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface UploadPhotoFormValues {
    maintainAspectRatio: boolean;
}

const CampaignCoverUploadPhotoForm = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const methods = Form.useForm<UploadPhotoFormValues>({
        defaultValues: {
            maintainAspectRatio: campaign?.maintainAspectRatio?.active ?? false,
        },
    });
    const { handleSubmit, control, path } = methods;
    const ref = useRef<UploadRef>(null);

    const { mutate: submit } = useMutation({
        mutationFn: (values: UploadPhotoFormValues) => {
            const data = {
                settings: [
                    {
                        code: Settings.MaintainAspectRatio.Code,
                        active: values.maintainAspectRatio,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaignId, data });
        },
        onSuccess: () => {
            toast.success('Updated cover photo settings!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update cover photo settings'),
    });

    const { isPending: isDeleting, mutate: deletePhoto } = useMutation({
        mutationFn: (url: string) => deleteCampaignMediaByUrl(campaignId, url),
        onSuccess: () => {
            toast.success('Deleted cover photo!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to delete cover photo'),
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<UploadPhotoFormValues>)}>
                <div className={'flex flex-col gap-4'}>
                    <Upload
                        ref={ref}
                        variant={'single'}
                        defaultFiles={
                            campaign?.cover?.type === 'image' && campaign.cover.url
                                ? [{ id: 'cover', url: campaign.cover.url }]
                                : []
                        }
                        acceptedFileTypes={['image/*']}
                        imagePreviewHeight={250}
                        allowReorder={false}
                        allowMultiple={false}
                        allowReplace={true}
                        // @ts-ignore
                        allowRemove={false}
                        strategy={'remote'}
                        uploadFn={async file => {
                            const { data } = await uploadCampaignMedia(campaignId, file, 'cover');

                            return data.url;
                        }}
                        onUpload={() => {
                            toast.success('Uploaded cover photo successfully!');
                            queryClient.invalidateQueries({
                                queryKey: campaignQueryKeys.campaign(campaignId),
                            });
                        }}
                        onUploadError={() => {
                            toast.error('Failed to upload cover photo');
                        }}
                        onremovefile={(_, file) => deletePhoto(file.getMetadata('url'))}
                    />
                    {campaign?.cover && (
                        <div>
                            <Button
                                variant="solid"
                                disabled={isDeleting}
                                onClick={() => ref.current?.removeFiles()}
                                color="error"
                                size="sm"
                            >
                                Remove cover photo
                            </Button>
                        </div>
                    )}

                    <div className={'flex items-center justify-between gap-2'}>
                        <Form.Label className={'text-base'} name={path('maintainAspectRatio')}>
                            Maintain aspect ratio
                        </Form.Label>
                        <Form.Switch control={control} name={'maintainAspectRatio'} />
                    </div>

                    <div>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

export { CampaignCoverUploadPhotoForm };
